import ErrorIcon from "@mui/icons-material/Error";
import * as React from "react";
import { useEffect } from "react";
import FilesService, { FileMediaSourceType } from "services/FilesService";
import { isUndefinedType } from "../../../utils/TypeGuards";
import AcxLoadingIndicator from "../AcxLoadingIndicator";
import MicroPlayerControl, { PlayStatus } from "./MicroPlayerControl";

enum LoadStatus {
    NotLoaded = 1,
    Loading,
    Loaded,
    Failed,
}

interface Props {
    getFunction?: () => Promise<string | Blob>;
    downloadMediaFileId?: string;
    mediaSourceType?: FileMediaSourceType;
    controlButtonClass?: string;
    pauseButtonClass?: string;
    disabled?: boolean;
    isLoading?: boolean;
    style?: React.CSSProperties;
    iconHeight?: number;
    iconWidth?: number;
}

const filesService = new FilesService();

function AcxMicroPlayer(props: Props) {
    const [playStatus, setPlayStatus] = React.useState<PlayStatus>(
        PlayStatus.Stopped,
    );
    const [loadStatus, setLoadStatus] = React.useState(LoadStatus.NotLoaded);
    const audioElement = React.useRef<HTMLAudioElement>(null);
    const objectUrlRef = React.useRef<string | null>(null);

    useEffect(() => {
        if (!isUndefinedType(props.isLoading)) {
            setLoadStatus(
                props.isLoading ? LoadStatus.Loading : LoadStatus.NotLoaded,
            );
        }
    }, [props.isLoading]);

    // Cleanup on unmount: revoke any object URLs
    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
            }
        };
    }, []);

    const onAudioPause = () => {
        setPlayStatus(PlayStatus.Paused);
    };

    const onAudioPlay = () => {
        setPlayStatus(PlayStatus.Play);
    };

    function onClick() {
        if (loadStatus === LoadStatus.NotLoaded) downloadFile();
        if (loadStatus === LoadStatus.Loaded) {
            let element = audioElement.current;

            if (!element) return;
            if (playStatus === PlayStatus.Play) {
                element.pause();
                setPlayStatus(PlayStatus.Paused);
            } else {
                element.play();
                setPlayStatus(PlayStatus.Play);
            }
        }
    }

    const onReady = React.useCallback(() => {
        if (audioElement.current) audioElement.current.play();
    }, []);

    const onLoadedData = () => {
        setLoadStatus(LoadStatus.Loaded);
    };

    const onError = () => {
        setLoadStatus(LoadStatus.Failed);
    };

    /**
     * Get a signed playback URL from the server and set it as the audio src.
     * The browser handles range requests natively — playback starts instantly
     * for files of any size, and works across all browsers.
     */
    const loadFromPlaybackUrl = async (
        mediaId: string,
        mediaSourceType: FileMediaSourceType = "audioFile",
    ) => {
        const playbackUrl = await filesService.getPlaybackUrl(
            mediaId,
            mediaSourceType,
        );

        if (audioElement.current) {
            audioElement.current.src = playbackUrl;
            audioElement.current.load();
        }
    };

    const downloadFile = async () => {
        setLoadStatus(LoadStatus.Loading);
        try {
            if (props.downloadMediaFileId) {
                await loadFromPlaybackUrl(
                    props.downloadMediaFileId,
                    props.mediaSourceType,
                );
                return;
            }

            const data: any = await props.getFunction?.();
            let blobUrl;

            if (typeof data === "string") {
                var binary_string = window.atob(data);
                var len = binary_string.length;
                var bytes = new Uint8Array(len);
                for (var i = 0; i < len; i++) {
                    bytes[i] = binary_string.charCodeAt(i);
                }

                let blob = new Blob([bytes], { type: "audio/wav" });

                blobUrl = URL.createObjectURL(blob);
            } else if (data instanceof Blob) {
                blobUrl = URL.createObjectURL(data);
            }

            if (audioElement.current && blobUrl) {
                objectUrlRef.current = blobUrl;
                audioElement.current.src = blobUrl;
                audioElement.current.load();
            }
        } catch (ex) {
            setLoadStatus(LoadStatus.Failed);
        }
    };

    useEffect(() => {
        if (audioElement.current && loadStatus === LoadStatus.Loaded) {
            onReady();
        }
    }, [loadStatus, onReady]);

    var content;

    if (loadStatus === LoadStatus.Failed) {
        content = <ErrorIcon color="error" />;
    } else if (loadStatus === LoadStatus.Loading) {
        content = (
            <div>
                <AcxLoadingIndicator
                    color="secondary"
                    alternate="PuffLoader"
                    size={!!props.iconHeight && props.iconHeight > 40 ? 64 : 32} // adjust loading size based on player button size
                />
            </div>
        );
    } else {
        content = (
            <MicroPlayerControl
                disabled={props.disabled}
                controlButtonClass={props.controlButtonClass}
                pauseButtonClass={props.pauseButtonClass}
                onClick={onClick}
                color={"primary"}
                status={playStatus}
                style={props.style}
                iconHeight={props.iconHeight}
                iconWidth={props.iconWidth}
            />
        );
    }

    return (
        <>
            {content}
            <audio
                onPlay={onAudioPlay}
                onPause={onAudioPause}
                onLoadedData={onLoadedData}
                onError={onError}
                ref={audioElement}
                controls
                hidden
                controlsList="nodownload"
            />
        </>
    );
}
export default AcxMicroPlayer;
